import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Table, TableBody } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import { processEnv } from 'components/configuration/environmentVariables'
import React from 'react'
import ViewPartnershipType from './ViewPartnershipType'
const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        margin: theme.spacing(5, 0),
    },
    cellItems: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    title: {
        color: '#04325B',
        fontWeight: 500,
        borderBottom: '6px solid #04325B',
        textTransform: 'uppercase',
    },

    tableContainer: {
        maxHeight: 248,
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    itemContainer: {
        width: '80%',
    },
    tableBorder: {
        '& .MuiTableCell-sizeSmall:last-child': {
            paddingLeft: 0,
            paddingTop: theme.spacing(1.25),
            paddingBottom: theme.spacing(1.25),
        },
    },
    linksHeading: {
        marginRight: theme.spacing(3),
        textTransform: 'uppercase',
        fontSize: pxToRem(13),
        fontWeight: 500,
        marginTop: theme.spacing(1.25),
        width: 85,
    },
}))
export type PartnerType = 'BIM' | 'Sustainability' | 'Swatchbox'
export type PartnerName =
    | 'BIMobject'
    | 'BIMsmith'
    | 'ULE'
    | 'Sustainable Minds'
    | 'Swatchbox'
export interface Partnership {
    level: string
    name: string
    partnerType: string
    url: string
}
const getLinkDetail = (
    partnerType: PartnerType,
    name: PartnerName,
    links: Partial<Partnership>[]
) => {
    return (
        links?.filter(
            (each) =>
                each.partnerType === partnerType &&
                each.name === name &&
                each.url !== ''
        )?.[0] ?? undefined
    )
}

export interface ViewPartnershipsProps {
    level?: ListingLevel
    partnerships?: Partial<Partnership>[]
}

export const ViewPartnerships: React.FC<ViewPartnershipsProps> = ({
    level,
    partnerships,
}) => {
    const classes = useStyles()
    if (!level || !partnerships) return null
    if (partnerships.length < 1) return null

    const bimLink = getLinkDetail('BIM', 'BIMsmith', partnerships)
    const swatchboxLink = getLinkDetail('Swatchbox', 'Swatchbox', partnerships)
    const ule = getLinkDetail('Sustainability', 'ULE', partnerships)
    const sustainability = getLinkDetail(
        'Sustainability',
        'Sustainable Minds',
        partnerships
    )

    if (!bimLink && !ule && !sustainability && !swatchboxLink) return null

    return (
        <GridContainer data-testid="dvLinks" className={classes.container}>
            <Typography variant="h3" className={classes.linksHeading}>
                Partnerships:
            </Typography>
            <GridItem className={classes.itemContainer}>
                <div className={classes.tableContainer}>
                    <Table className={classes.tableBorder} size="small">
                        <TableBody>
                            {bimLink && (
                                <TableRow className={classes.tableBorder}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="justify"
                                        className={classes.cellItems}
                                    >
                                        <ViewPartnershipType
                                            label="BIMsmith"
                                            xs={4}
                                            link={bimLink?.url}
                                            level={level}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}

                            {!!Number(processEnv.REACT_APP_ShowSwatchbox) &&
                                swatchboxLink && (
                                    <TableRow className={classes.tableBorder}>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            align="justify"
                                            className={classes.cellItems}
                                        >
                                            <ViewPartnershipType
                                                label="Swatchbox"
                                                xs={4}
                                                link={swatchboxLink?.url}
                                                level={level}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}
                            {ule && (
                                <TableRow className={classes.tableBorder}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="justify"
                                        className={classes.cellItems}
                                    >
                                        <ViewPartnershipType
                                            label="ULE"
                                            xs={2}
                                            link={ule?.url}
                                            level={level}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}

                            {sustainability && (
                                <TableRow className={classes.tableBorder}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="justify"
                                        className={classes.cellItems}
                                    >
                                        <ViewPartnershipType
                                            label="Sustainable Minds"
                                            xs={6}
                                            link={sustainability?.url}
                                            level={level}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </GridItem>
        </GridContainer>
    )
}

export default ViewPartnerships
